<template>
    <div class="ClassCart">
        <div class="ClassCart--title">
            <div class="ClassCart--title-name text-truncate mb-1"
                 :title="activity.className">
                {{activity.className}}
            </div>
            <template v-if="activity.price === 0">
                <div class="ClassCart--title-price text-green">FREE</div>
            </template>
            <template v-else>
                <div class="ClassCart--title-price">
                    <template v-if="activity.billingType === 'SESSION'">
                        <span>{{schoolCurrency | currencySign}} {{activity.price | priceFormat}}</span>
                    </template>
                    <template v-else>
                        <span>{{schoolCurrency | currencySign}} {{activity.price | priceFormat}} / {{activity.billingType | toCapitalize}}</span>
                    </template>
                </div>
            </template>
        </div>

        <div class="ClassCart--days">
            <div :class="{disabled: !activity.sunday}">Sun</div>
            <div :class="{disabled: !activity.monday}">Mon</div>
            <div :class="{disabled: !activity.tuesday}">Tue</div>
            <div :class="{disabled: !activity.wednesday}">Wed</div>
            <div :class="{disabled: !activity.thursday}">Thu</div>
            <div :class="{disabled: !activity.friday}">Fri</div>
            <div :class="{disabled: !activity.saturday}">Sat</div>
        </div>

        <div class="ClassCart--location">
            <div class="d-flex justify-content-center align-items-center mr-1">
                <template v-if="activity.zoom">
                    <img src="../../../../assets/img/svg/zoom.svg">
                </template>
                <template v-else>
                    <img src="../../../../assets/img/svg/map-pin.svg">
                </template>
            </div>
            <span class="ClassCart--location-name text-truncate w-75">{{activity.locationName }}</span>
        </div>

        <div class="ClassCart--content">
            <div class="ClassCart--content-info">
                <div>
                    <span v-if="schoolCountry === 'England'"
                          class="ClassCart--content-info-title">Age/Year: </span>
                    <span v-else
                          class="ClassCart--content-info-title">Age/Grades: </span>
                    {{range}}
                </div>
                <div>
                    <span class="ClassCart--content-info-title">Time: </span>{{activity.timeFrom | timeFormatAmPm}} - {{activity.timeTo | timeFormatAmPm}}
                </div>
                <div>
                    <span class="ClassCart--content-info-title">Dates: </span>{{activity.classStartDate | timeFormatL}} - {{activity.classEndDate | timeFormatL}}
                </div>
                <div>
                    <span class="ClassCart--content-info-title">Max: </span>{{activity.maxEnroll}}
                </div>
                <transition name="fade">
                    <div v-if="!isOpened"
                         class="description-shadow"
                    />
                </transition>
                <template v-if="detectIE">
                    <transition name="expand">
                        <div v-show="isOpened">
                            <div>
                                <span class="ClassCart--content-info-title">Instructor: </span>{{activity.instructor}}
                            </div>
                            <div>
                                <span class="ClassCart--content-info-title">Description: </span>
                                <div v-html="activity.description"/>
                            </div>
                        </div>
                    </transition>
                </template>
                <template v-else>
                    <transition name="expand"
                                @enter="onEnter"
                                @after-enter="onAfterEnter"
                                @leave="onLeave">
                        <div v-show="isOpened">
                            <div>
                                <span class="ClassCart--content-info-title">Instructor: </span>{{activity.instructor}}
                            </div>
                            <div>
                                <span class="ClassCart--content-info-title">Description: </span>
                                <div v-html="activity.description"/>
                            </div>
                        </div>
                    </transition>
                </template>
            </div>
            <div class="ClassCart--content-more"
                 @click="toggleCart"
            >
                <i class="material-icons down" :class="isOpened ? 'rotated': 'rotatedBack'">keyboard_arrow_down</i>
            </div>
            <div class="ClassCart--content-button">
                <button :class="['primary-btn', activity.status === 'Full' && 'ClassCart--content-button-waitlist']"
                        :style="{'width':'200px'}"
                        :disabled="activity.status === 'Closed'"
                        @click.prevent="$emit('open')"
                >
                    <template v-if="activity.status === 'Full'"> Waitlist </template>
                    <template v-else-if="activity.status === 'Online'"> Enroll </template>
                    <template v-else-if="activity.status === 'Closed'"> Closed </template>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { GRADES } from '@/constants/grades';
    import { AGES } from '@/constants/school-ages';
    import { ExpandAnimationMixin } from '@/mixins/expandAnimationMixin';

    export default {
        name: 'ClassCart',

        props: {
            activity: {
                type: Object,
                required: false
            }
        },

        data() {
            return {
                isOpened: false
            }
        },

        computed: {
            ...mapGetters(['schoolCurrency', 'schoolCountry']),

            range() {
                let rangeFrom = null,
                    rangeTo = null;

                if (this.activity.gradeFrom) {
                    rangeFrom = GRADES.find(el => el.info === this.activity.gradeFrom).view;
                    rangeTo = GRADES.find(el => el.info === this.activity.gradeTo).view;
                }
                else {
                    rangeFrom = AGES.find(el => el.data === this.activity.ageFrom).view;
                    rangeTo = AGES.find(el => el.data === this.activity.ageTo).view;
                }

                return rangeFrom === rangeTo
                            ? rangeFrom
                            : `${rangeFrom} - ${rangeTo}`;
            },

            detectIE() {
                return window.navigator.userAgent.indexOf('MSIE') > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
            }
        },

        mixins: [ExpandAnimationMixin],

        methods: {
            toggleCart() {
                this.isOpened = !this.isOpened;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../assets/styles/variables/colors';

    .ClassCart {
        width: 100%;
        max-width: 340px;
        margin: 0 auto;
        padding: 28px 20px 20px;
        border-radius: 4px;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.15);
        background-color: $White;

        @media screen and (max-width: 700px) {
            width: 95%;
        }

        &--title {
            width: 100%;

            &-name {
                cursor: default;
                font-size: 25px;
                font-weight: 700;
                line-height: 1.33;
                color: $Dark;
            }

            &-price {
                font-size: 18px;
                font-weight: 700;
                line-height: 2;
                color: $SoftBlue;
            }
        }

        &--days {
            margin-top: 20px;
            display: flex;
            justify-content: space-between;

            > div {
                width: 100%;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 2px;
                font-size: 11px;
                font-weight: 400;
                color: $ShoppingCartTextColor;
                background-color: $ComplementaryBgTxtDarker;

                &:not(:last-child) {
                    margin-right: 6px;
                }
            }

            .disabled {
                color: $WhiteGrey;
                background-color: $ComplementaryBackgroundLightGrey;
            }
        }

        &--location {
            display: flex;
            align-items: center;
            margin-top: 20px;

            img {
                height: 16px;
                width: 16px;
            }

            &-name {
                font-weight: 400;
                font-size: 14px;
                color: $Light-dark;
            }
        }

        &--content {
            &-info {
                position: relative;
                font-size: 15px;
                font-weight: 400;
                line-height: 1.6;
                color: $Light-dark;
                word-break: break-word;
                @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {  //for IE
                    word-break: break-all;
                }

                .description-shadow {
                    position: absolute;
                    bottom: 0;
                    width: 100%;
                    height: 49px;
                    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.54) 54%, #ffffff)
                }

                &-title {
                    font-weight: bold;
                    color: $WhiteGrey;
                }
            }

            &-more {
                width: 40px;
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: 0 auto;
                border-radius: 50%;
                transform: rotate(180deg);
                box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.2);
                cursor: pointer;
                background-color: $White;

                i {
                    position: relative;
                    display: block;
                    font-size: 28px;
                    color: $SoftBlue;

                    &:hover {
                        color: $BlueActive;
                    }
                }
            }

            &-button {
                margin-top: 23px;
                display: flex;
                justify-content: center;

                &-waitlist {
                    width: 200px;
                    color: $White;
                    background-color: $NotificationError;

                    &:hover {
                        background-color: $SimplyAfterSchoolLightRed;
                    }
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/dashboard/pages/Activities/ClassCart.vue